import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import { toBlob as htmlToImageBlob } from 'html-to-image';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Package,
  RefreshCw,
  Check,
  Layers,
  Coins,
  Wand2,
  Sparkles,
  FolderOpen,
  Plus,
  X,
  Pencil,
  ImageIcon,
  Trash2,
  ArrowDownUp,
  Maximize2,
  Download,
  Zap,
} from 'lucide-react';
import MainNavigation from '../components/MainNavigation';
import ThemeToggle from '../components/ThemeToggle';
import { FusionUploadScreen } from '../components/carousel/FusionWizard';
import { TopicsRegenerateModal } from '../components/carousel/TopicsChatSidebar';
import { CarouselPagesChat } from '../components/carousel/CarouselPagesChat';
import InlineTextEditModal from '../components/carousel/InlineTextEditModal';
import BgImageMenu from '../components/carousel/BgImageMenu';
import { useAuth } from '../contexts/AuthContext';
import { composeHtmlWithBg } from '../utils/composeHtmlBg';

const API = process.env.REACT_APP_BACKEND_URL;

// Estimated credit cost for full 7-page carousel generation (Prompt B · Opus 4.7).
// Range tuned to recent production samples + fusion pipeline add-ons (Opus plan
// + Nano Banana images) when the user chooses to brand pages after generation.
const GENERATE_COST_ESTIMATE = { low: 30, high: 60 };

// Multi-stage progress steps shown during Prompt B generation (~60-120s).
// Pure frontend timeline since Opus 4.7 single call has no intermediate events.
const GENERATION_STAGES = [
  { label: '讀取 IP 定位與目標人群', etaSec: 6 },
  { label: '規劃 7 頁敘事弧線與情緒曲線', etaSec: 12 },
  { label: '設計封面 Hook（C1-C45 自我審查）', etaSec: 15 },
  { label: '撰寫正文段落與金句頁', etaSec: 22 },
  { label: '規劃封面 / CTA 視覺語言', etaSec: 14 },
  { label: '組合設計並套用樣式', etaSec: 10 },
  { label: '最終品質審查中，差啲就得', etaSec: 999 },
];
const SLIDE_W = 1080;
const SLIDE_H = 1350;

const FUNNEL_OPTIONS = [
  { id: 'traffic', label: '流量型', desc: '引導儲存／分享' },
  { id: 'persona', label: '人設型', desc: '引導追蹤' },
  { id: 'conversion', label: '轉化型', desc: '引導 DM／留言' },
];

const LANGUAGE_OPTIONS = [
  { id: 'zh-TW', label: '書面語' },
  { id: 'yue', label: '粵語' },
];

const CAROUSEL_FORM_STORAGE_KEY = 'reelscript:carousel:lastForm';

function loadLastForm() {
  try {
    const raw = localStorage.getItem(CAROUSEL_FORM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) { /* ignore */ }
  return null;
}

function saveLastForm(form) {
  try {
    // Don't persist the topic itself (new project = new topic) — keep IP/audience/handle etc.
    const { topic: _omitTopic, ...rest } = form || {};
    void _omitTopic;
    localStorage.setItem(CAROUSEL_FORM_STORAGE_KEY, JSON.stringify(rest));
  } catch (_) { /* ignore storage full */ }
}

const DEFAULT_FORM = {
  topic: '',
  ip_positioning: '',
  target_audience: '',
  instagram_handle: '',
  funnel: 'traffic',
  language: 'zh-TW',
  language_style: '',
};

const LANGUAGE_STYLE_PRESETS = [
  '輕鬆口語',
  '專業嚴謹',
  '感性細膩',
  '犀利挑釁',
  '溫柔陪伴',
  '冷幽默',
];

const DNA_COLORS = {
  認知翻轉: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
  實用收藏: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  身份共鳴: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  痛點拆解: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  經歷投射: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  選擇指南: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
};

const Stepper = ({ step, topicsCount = 0, pagesCount = 0, onNavigate }) => (
  <div className="flex items-center gap-4 mb-14" data-testid="carousel-stepper">
    {[
      { n: 1, label: '輸入主題', available: true },
      { n: 2, label: '揀選題', available: true },
      { n: 3, label: '最終成果', available: pagesCount > 0 },
    ].map((s, i) => {
      const active = step === s.n;
      const done = step > s.n;
      const clickable = !!onNavigate && (s.available || s.n === 1) && !active;
      const interactive = clickable ? 'cursor-pointer' : '';
      return (
        <React.Fragment key={s.n}>
          <button
            type="button"
            disabled={!clickable}
            onClick={() => clickable && onNavigate(s.n)}
            className={`flex items-center gap-2.5 transition-colors group ${interactive} disabled:cursor-default`}
            data-testid={`carousel-step-${s.n}`}
          >
            <div
              className={`relative w-8 h-8 rounded-full flex items-center justify-center text-[12px] transition-all duration-300 ${
                active
                  ? 'bg-atelier-coral text-white border border-atelier-coral'
                  : done
                  ? 'bg-transparent border border-atelier-coral/50 text-atelier-coral'
                  : 'bg-transparent border border-atelier-line text-atelier-subtle'
              } ${clickable ? 'group-hover:border-atelier-coral group-hover:text-atelier-coral' : ''}`}
              style={{
                fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
                fontWeight: 500,
              }}
            >
              {done ? <Check className="w-4 h-4" strokeWidth={2} /> : s.n}
              {active && (
                <span className="absolute -inset-1 rounded-full border border-atelier-coral/30 atelier-breathe" />
              )}
            </div>
            <span
              className={`atelier-caption hidden sm:inline transition-colors ${
                active
                  ? 'text-atelier-ink'
                  : done
                  ? 'text-atelier-mute'
                  : 'text-atelier-subtle'
              } ${clickable ? 'group-hover:text-atelier-coral' : ''}`}
            >
              {s.label}
            </span>
          </button>
          {i < 2 && (
            <div
              className={`flex-1 h-px transition-colors duration-500 ${
                step > s.n ? 'bg-atelier-coral/40' : 'bg-atelier-line'
              }`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const CreditBadge = ({ charged, label = '消耗' }) => (
  <span
    data-testid="carousel-credit-badge"
    className="inline-flex items-center gap-1.5 text-[11px] font-medium text-lime-400 bg-lime-500/10 border border-lime-500/30 px-2.5 py-1 rounded-full"
  >
    <Coins className="w-3 h-3" />
    {label} {charged.toFixed(2)} 點數
  </span>
);

// --- Screen 1 -------------------------------------------------------------
const Screen1Input = ({ form, setForm, onNext, loading }) => (
  <section data-testid="carousel-screen-1" className="max-w-2xl mx-auto">
    {/* Editorial kicker */}
    <div className="flex items-center gap-3 mb-6">
      <span className="h-px w-8 bg-atelier-lineHi" />
      <span className="atelier-caption">Studio · Brief</span>
      <span className="w-1.5 h-1.5 rounded-full bg-atelier-coral atelier-breathe" />
    </div>

    <h2
      className="text-3xl md:text-5xl mb-4 text-atelier-ink tracking-[-0.02em] leading-[1.05]"
      style={{
        fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
        fontWeight: 500,
      }}
    >
      輪播
      <em
        className="not-italic"
        style={{ color: 'var(--atelier-coral)', fontStyle: 'italic' }}
      >
        工廠
      </em>
    </h2>
    <p className="text-atelier-mute text-[15px] mb-12" style={{ lineHeight: 1.8 }}>
      填 IP 定位 + 主題方向，AI 會根據你的身份同聲音生成
      <span className="text-atelier-ink font-medium"> 10 個高品質選題</span>。
    </p>

    <div className="space-y-7">
      {/* 主題方向 */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <label className="atelier-caption">主題方向</label>
          <span className="atelier-caption text-atelier-subtle">01 · Topic</span>
        </div>
        <textarea
          data-testid="carousel-input-topic"
          value={form.topic}
          onChange={(e) => setForm({ ...form, topic: e.target.value })}
          placeholder="例：想介紹情緒管理 / 新手理財陷阱 / 親密關係中的溝通誤區"
          rows={2}
          className="w-full bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-xl px-4 py-3.5 text-atelier-ink text-[15px] outline-none resize-y transition-colors placeholder:text-atelier-subtle"
        />
      </div>

      {/* IP 定位 */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <label className="atelier-caption">IP 定位 <span className="text-atelier-subtle normal-case tracking-normal">（你是誰 · 品牌聲音 · 視覺氣質）</span></label>
          <span className="atelier-caption text-atelier-subtle">02 · Identity</span>
        </div>
        <textarea
          data-testid="carousel-input-ip"
          value={form.ip_positioning}
          onChange={(e) => setForm({ ...form, ip_positioning: e.target.value })}
          placeholder={`例：
我是情緒教練 Cherry，專門陪伴 30+ 女性處理人際內耗。
品牌聲音：溫柔而不失冷靜，像閨蜜與心理師的結合。
視覺氣質：微霧奶油色調、襯線字型、留白豐富。`}
          rows={5}
          className="w-full bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-xl px-4 py-3.5 text-atelier-ink text-[15px] outline-none resize-y transition-colors placeholder:text-atelier-subtle"
          style={{ lineHeight: 1.75 }}
        />
      </div>

      {/* 目標人群 */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <label className="atelier-caption">目標人群 <span className="text-atelier-subtle normal-case tracking-normal">（這組內容在為誰而寫）</span></label>
          <span className="atelier-caption text-atelier-subtle">03 · Audience</span>
        </div>
        <textarea
          data-testid="carousel-input-audience"
          value={form.target_audience}
          onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
          placeholder="例：30-40 歲在職女性，高敏感特質，理性但情緒消耗大，關注自我成長"
          rows={2}
          className="w-full bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-xl px-4 py-3.5 text-atelier-ink text-[15px] outline-none resize-y transition-colors placeholder:text-atelier-subtle"
        />
      </div>

      {/* Instagram */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <label className="atelier-caption">Instagram 帳號 <span className="text-atelier-subtle normal-case tracking-normal">（選填 · 印在每頁底部）</span></label>
          <span className="atelier-caption text-atelier-subtle">04 · Handle</span>
        </div>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-atelier-subtle text-[15px] pointer-events-none">@</span>
          <input
            type="text"
            data-testid="carousel-input-instagram"
            value={form.instagram_handle}
            onChange={(e) => {
              const v = e.target.value.replace(/^@+/, '').trim();
              setForm({ ...form, instagram_handle: v });
            }}
            placeholder="mia.wealth"
            className="w-full bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-xl pl-9 pr-4 py-3 text-atelier-ink text-[15px] outline-none transition-colors placeholder:text-atelier-subtle"
          />
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 pt-3">
        <span className="h-px flex-1 bg-atelier-line" />
        <span className="atelier-caption">Structure</span>
        <span className="h-px flex-1 bg-atelier-line" />
      </div>

      {/* 漏斗類型 — card-style */}
      <div>
        <label className="atelier-caption mb-3 block">漏斗類型</label>
        <div className="grid grid-cols-3 gap-2.5">
          {FUNNEL_OPTIONS.map((f) => {
            const selected = form.funnel === f.id;
            return (
              <button
                key={f.id}
                data-testid={`carousel-funnel-${f.id}`}
                onClick={() => setForm({ ...form, funnel: f.id })}
                className={`relative text-left px-4 py-3.5 rounded-xl border transition-all duration-300 ${
                  selected
                    ? 'border-atelier-coral bg-atelier-coral/[0.06] text-atelier-ink'
                    : 'border-atelier-line bg-atelier-surface text-atelier-mute hover:border-atelier-lineHi hover:text-atelier-ink'
                }`}
              >
                {selected && (
                  <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full bg-atelier-coral" />
                )}
                <div
                  className={`text-[15px] mb-0.5 ${selected ? 'text-atelier-ink' : ''}`}
                  style={{
                    fontFamily: selected ? "'Noto Serif TC', 'Playfair Display', Georgia, serif" : 'inherit',
                    fontWeight: selected ? 500 : 500,
                  }}
                >
                  {f.label}
                </div>
                <div className="text-[11px] text-atelier-subtle mt-1">{f.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 語言 — pill */}
      <div>
        <label className="atelier-caption mb-3 block">語言</label>
        <div className="grid grid-cols-2 gap-2.5">
          {LANGUAGE_OPTIONS.map((l) => {
            const selected = form.language === l.id;
            return (
              <button
                key={l.id}
                data-testid={`carousel-lang-${l.id}`}
                onClick={() => setForm({ ...form, language: l.id })}
                className={`relative px-4 py-3 rounded-full border text-[14px] transition-all duration-300 ${
                  selected
                    ? 'border-atelier-coral bg-atelier-coral/[0.06] text-atelier-ink'
                    : 'border-atelier-line bg-atelier-surface text-atelier-mute hover:border-atelier-lineHi hover:text-atelier-ink'
                }`}
                style={{ fontWeight: 500 }}
              >
                {selected && (
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-atelier-coral mr-2 align-middle" />
                )}
                {l.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 語言風格 */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <label className="atelier-caption">語言風格 <span className="text-atelier-subtle normal-case tracking-normal">（選填 · 調性）</span></label>
        </div>
        <input
          type="text"
          data-testid="carousel-input-language-style"
          value={form.language_style}
          onChange={(e) => setForm({ ...form, language_style: e.target.value })}
          placeholder="自由輸入或點選下方預設"
          className="w-full bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-xl px-4 py-3 text-atelier-ink text-[15px] outline-none transition-colors placeholder:text-atelier-subtle"
        />
        <div className="flex flex-wrap gap-1.5 mt-3">
          {LANGUAGE_STYLE_PRESETS.map((s) => {
            const selected = form.language_style === s;
            return (
              <button
                key={s}
                type="button"
                data-testid={`carousel-lang-style-preset-${s}`}
                onClick={() => setForm({ ...form, language_style: s })}
                className={`text-[11px] px-2.5 h-7 rounded-full border transition-all duration-200 ${
                  selected
                    ? 'bg-atelier-coral/10 border-atelier-coral/50 text-atelier-coral'
                    : 'bg-atelier-surface border-atelier-line text-atelier-mute hover:border-atelier-lineHi hover:text-atelier-ink'
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>
      <button
        data-testid="carousel-next-btn"
        onClick={onNext}
        disabled={loading || !form.topic.trim()}
        className="w-full atelier-btn-primary atelier-glow-coral h-14 rounded-full text-[15px] disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ fontWeight: 500 }}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> 串流生成中…
          </>
        ) : (
          <>
            生 10 個選題 <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </>
        )}
      </button>
    </div>
  </section>
);

// --- Screen 2 -------------------------------------------------------------
const TopicCard = ({ t, i, onPick }) => (
  <motion.button
    layout
    initial={{ opacity: 0, y: 16, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -2 }}
    data-testid={`carousel-topic-card-${i}`}
    onClick={() => onPick(t)}
    className="text-left atelier-card p-5 hover:border-atelier-coral/50 transition-all duration-300 group relative overflow-hidden"
  >
    {/* Number */}
    <div
      className="absolute top-3 right-4 tabular-nums text-atelier-subtle/70 group-hover:text-atelier-coral/80 transition-colors"
      style={{
        fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
        fontSize: '28px',
        fontWeight: 400,
        lineHeight: 1,
      }}
    >
      {String(i + 1).padStart(2, '0')}
    </div>

    <div className="flex flex-wrap gap-1.5 mb-4 pr-10">
      <span
        className={`text-[10px] tracking-wider font-semibold px-2 py-0.5 rounded-full border ${
          DNA_COLORS[t.dna] || 'bg-atelier-surface2 text-atelier-mute border-atelier-line'
        }`}
      >
        {t.dna}
      </span>
      <span className="text-[10px] tracking-wider font-semibold px-2 py-0.5 rounded-full bg-atelier-surface2 text-atelier-mute border border-atelier-line">
        {t.arc}
      </span>
    </div>
    <h3
      className="text-atelier-ink text-[17px] mb-2.5 leading-snug tracking-tight group-hover:text-atelier-coral transition-colors"
      style={{
        fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
        fontWeight: 500,
      }}
    >
      {t.title}
    </h3>
    <p className="text-[13px] text-atelier-ink/85 whitespace-pre-line mb-3 leading-[1.7]">
      {t.hook_preview}
    </p>
    <p className="text-[11px] text-atelier-subtle italic border-l-2 border-atelier-line pl-2.5">
      {t.arc_direction}
    </p>
  </motion.button>
);

const NARRATIVE_STEPS = [
  { label: '讀取你的 IP 定位與品牌聲音', icon: '◉' },
  { label: '分析目標人群的痛點與情緒地圖', icon: '◈' },
  { label: '配對互動 DNA（6 種）與敘事弧線（7 條）', icon: '◇' },
  { label: '裸跑 20 條候選 hook，5 維度評分', icon: '◆' },
  { label: '對應 45 個公式 C1-C45，封面四維檢查', icon: '◎' },
  { label: '組裝 10 個最強選題⋯', icon: '✶' },
];

const NarrativeLoader = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setActiveIdx((i) => Math.min(i + 1, NARRATIVE_STEPS.length - 1));
    }, 1600);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      className="mb-6 atelier-card p-6"
      data-testid="carousel-narrative-loader"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="atelier-oil-loader" style={{ width: 20, height: 20 }} />
        <span className="atelier-caption text-atelier-coral">AI · Studio at work</span>
        <span className="h-px flex-1 bg-atelier-line" />
      </div>
      <div className="space-y-3">
        {NARRATIVE_STEPS.map((s, i) => {
          const done = i < activeIdx;
          const active = i === activeIdx;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, x: -8 }}
              animate={{
                opacity: done ? 0.55 : active ? 1 : 0.3,
                x: 0,
              }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
              className="flex items-center gap-3 text-[13.5px]"
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] flex-shrink-0 transition-all duration-500 ${
                  done
                    ? 'border border-atelier-coral/50 text-atelier-coral bg-atelier-coral/5'
                    : active
                      ? 'border border-atelier-coral text-white bg-atelier-coral atelier-breathe'
                      : 'border border-atelier-line text-atelier-subtle bg-atelier-surface'
                }`}
              >
                {done ? <Check className="w-3 h-3" strokeWidth={2} /> : s.icon}
              </span>
              <span
                className={`transition-colors duration-500 ${
                  done
                    ? 'text-atelier-mute line-through decoration-atelier-line decoration-1'
                    : active
                      ? 'text-atelier-ink'
                      : 'text-atelier-subtle'
                }`}
                style={{
                  fontFamily: active ? "'Noto Serif TC', 'Playfair Display', Georgia, serif" : 'inherit',
                  fontWeight: active ? 500 : 400,
                }}
              >
                {s.label}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const Screen2Topics = ({
  topics,
  streaming,
  creditsCharged,
  onPick,
  onBack,
  onOpenRegen,
  onFreshGenerate,
}) => (
  <section data-testid="carousel-screen-2" className="max-w-5xl mx-auto">
    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight">
            {streaming ? '選題生成中' : '選擇一個最吸引的選題'}
          </h2>
          {!streaming && creditsCharged > 0 && <CreditBadge charged={creditsCharged} />}
        </div>
        <p className="text-obsidian-400 text-sm mt-1">
          {streaming
            ? `AI 正在逐條生成 (${topics.length}/10)...`
            : '10 個選題涵蓋多種互動 DNA。Click 一張繼續，或按右上「重新生成」補充方向再生一批。'}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onBack}
          disabled={streaming}
          className="flex items-center gap-1.5 text-xs text-obsidian-300 hover:text-white px-3 py-2 rounded-md border border-obsidian-600 hover:border-obsidian-400 disabled:opacity-40"
          data-testid="carousel-topics-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          重填
        </button>
        <button
          onClick={onOpenRegen}
          disabled={streaming || !topics.length}
          className="flex items-center gap-1.5 text-xs font-semibold text-obsidian-900 bg-lime-500 hover:bg-lime-400 disabled:bg-obsidian-700 disabled:text-obsidian-400 px-3 py-2 rounded-md transition-colors"
          data-testid="carousel-topics-regen"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          重新生成
        </button>
      </div>
    </div>

    {streaming && topics.length === 0 && <NarrativeLoader />}

    {/* Empty state when user lands on step 2 from a drawer-loaded old deck
        that was saved before topic_candidates were persisted. */}
    {!streaming && topics.length === 0 && (
      <div
        className="rounded-xl border border-dashed border-obsidian-700 bg-obsidian-950/40 p-8 text-center"
        data-testid="carousel-topics-empty"
      >
        <p className="text-obsidian-300 text-sm mb-4">
          此組別未保存原本的 10 條選題候選（舊組別未有儲存功能），你可以：
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button
            onClick={onBack}
            className="inline-flex items-center justify-center gap-1.5 text-xs text-obsidian-200 hover:text-white px-4 py-2 rounded-md border border-obsidian-600 hover:border-obsidian-400"
            data-testid="carousel-topics-empty-restart"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            返回填表單，重新生成
          </button>
          <button
            onClick={onFreshGenerate}
            className="inline-flex items-center justify-center gap-1.5 text-xs text-obsidian-950 bg-lime-500 hover:bg-lime-400 px-4 py-2 rounded-md font-medium"
            data-testid="carousel-topics-empty-regen"
          >
            <Sparkles className="w-3.5 h-3.5" />
            用當前設定生成 10 條新選題
          </button>
        </div>
      </div>
    )}

    <div className="grid md:grid-cols-2 gap-3">
      <AnimatePresence initial={false}>
        {topics.map((t, i) => (
          <TopicCard key={`${t.title}-${i}`} t={t} i={i} onPick={onPick} />
        ))}
      </AnimatePresence>

      {streaming && topics.length > 0 &&
        Array.from({ length: Math.max(0, 10 - topics.length) }).map((_, i) => (
          <div
            key={`skeleton-${i}`}
            className="rounded-xl border border-dashed border-obsidian-700 bg-obsidian-950/30 p-5 h-[152px] flex items-center justify-center"
            data-testid={`carousel-topic-skeleton-${i}`}
          >
            <div className="flex items-center gap-2 text-obsidian-500 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-obsidian-600 animate-pulse" />
            </div>
          </div>
        ))}
    </div>
  </section>
);

// --- Screen 3: Scaled iframe preview --------------------------------------
const SlidePreview = ({ page, index, registerRef, onAction, onEnlarge, onEditText, onBgAction, totalPages }) => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(0.25);

  useEffect(() => {
    if (iframeRef.current) registerRef(index, iframeRef.current);
  }, [index, registerRef]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const compute = () => {
      const w = el.clientWidth;
      setScale(w / SLIDE_W);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const actions = onAction
    ? [
        { Icon: Maximize2, label: '放大', onClick: () => onEnlarge && onEnlarge(index) },
        // Inline text edit (C2, Apr 2026) — direct DOM text-swap, no AI, no
        // credit cost.  Opens a side-by-side modal with live iframe preview.
        onEditText && { Icon: Pencil, label: '編輯文字', onClick: () => onEditText(page) },
        // Manual bg image control (Feature 2, Apr 2026) — 3 sub-actions:
        // 🎲 AI regenerate / 📤 upload / ❌ clear.  Opens BgImageMenu popover.
        onBgAction && {
          Icon: ImageIcon,
          label: '換背景',
          onClick: (e) => onBgAction(page, e.currentTarget.getBoundingClientRect()),
        },
        { Icon: Plus, label: '插入新頁', prompt: `在第 ${index} 頁後面插入一頁新內容，請建議寫什麼？` },
        totalPages > 1 && { Icon: Trash2, label: '刪除', prompt: `刪除 p${index}`, danger: true },
        totalPages > 1 && { Icon: ArrowDownUp, label: '調位', prompt: `幫我將 p${index} 搬到第 1 頁之前` },
      ].filter(Boolean)
    : [];

  const handleContainerClick = () => {
    if (onEnlarge) onEnlarge(index);
  };

  return (
    <div className="flex flex-col items-center group/slide relative" data-testid={`carousel-slide-${index}`}>
      <div
        ref={containerRef}
        onClick={handleContainerClick}
        className="relative rounded-xl overflow-hidden border border-obsidian-600 bg-white shadow-lg w-full cursor-zoom-in"
        style={{ aspectRatio: `${SLIDE_W}/${SLIDE_H}` }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && onEnlarge) {
            e.preventDefault();
            onEnlarge(index);
          }
        }}
        data-testid={`carousel-slide-${index}-enlarge-trigger`}
      >
        <iframe
          ref={iframeRef}
          srcDoc={composeHtmlWithBg(page.html, page.bg_image)}
          sandbox="allow-same-origin"
          title={`slide-${index}`}
          scrolling="no"
          style={{
            width: `${SLIDE_W}px`,
            height: `${SLIDE_H}px`,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
            backgroundColor: '#ffffff',
          }}
        />

        {/* Lucide action overlay — desktop: top-right on hover; mobile: always-visible bottom strip */}
        {actions.length > 0 && (
          <>
            <div
              className="hidden md:flex absolute top-2 right-2 gap-1 bg-obsidian-950/90 backdrop-blur-md border border-obsidian-600 rounded-lg px-1 py-1 opacity-0 group-hover/slide:opacity-100 transition-all duration-150 z-10 shadow-lg shadow-black/40"
              data-testid={`carousel-slide-${index}-actions`}
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map(({ Icon, label, prompt, danger, onClick }) => (
                <button
                  key={label}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onClick) onClick(e);
                    else if (prompt) onAction(prompt);
                  }}
                  aria-label={label}
                  title={label}
                  className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                    danger
                      ? 'text-red-300 hover:text-red-200 hover:bg-red-500/15'
                      : 'text-obsidian-200 hover:text-lime-300 hover:bg-lime-500/10'
                  }`}
                  data-testid={`carousel-slide-${index}-action-${label}`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              ))}
            </div>
            <div
              className="md:hidden absolute bottom-2 left-2 right-2 flex gap-0.5 bg-obsidian-950/90 backdrop-blur-md border border-obsidian-600 rounded-lg px-1 py-1 z-10 justify-around shadow-lg shadow-black/40"
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map(({ Icon, label, prompt, danger, onClick }) => (
                <button
                  key={label}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onClick) onClick(e);
                    else if (prompt) onAction(prompt);
                  }}
                  aria-label={label}
                  className={`flex-1 h-7 rounded-md flex items-center justify-center transition-colors ${
                    danger
                      ? 'text-red-300 active:bg-red-500/15'
                      : 'text-obsidian-200 active:text-lime-300 active:bg-lime-500/10'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="mt-2 text-[10px] text-obsidian-400">第 {index} 頁</div>
    </div>
  );
};

const Screen3Preview = ({ pages, setPages, carouselId, emotionalArc, ctaDesign, hookAudit, designRationale, creditsCharged, creditsRemaining, isBranded, onRestart, onBack, onSwitchClean, onRefreshUser, onFusionComplete, onAddCredits, onEditText, onBgAction }) => {
  const iframeRefs = useRef({});
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  // pendingChatPrompt: {text, ts} — bumped whenever inline page action is clicked; CarouselPagesChat auto-sends on ts change.
  const [pendingChatPrompt, setPendingChatPrompt] = useState(null);
  const [enlargedIndex, setEnlargedIndex] = useState(null);
  const [downloadingPng, setDownloadingPng] = useState(false);
  const enlargedIframeRef = useRef(null);
  const sendToChat = (text) => setPendingChatPrompt({ text, ts: Date.now() });
  const enlargedPage = enlargedIndex != null ? pages.find((p) => p.index === enlargedIndex) : null;

  // ESC to close enlarged modal
  useEffect(() => {
    if (enlargedIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setEnlargedIndex(null);
      if (e.key === 'ArrowRight') {
        setEnlargedIndex((cur) => {
          const idxs = pages.map((p) => p.index);
          const i = idxs.indexOf(cur);
          return i >= 0 && i < idxs.length - 1 ? idxs[i + 1] : cur;
        });
      }
      if (e.key === 'ArrowLeft') {
        setEnlargedIndex((cur) => {
          const idxs = pages.map((p) => p.index);
          const i = idxs.indexOf(cur);
          return i > 0 ? idxs[i - 1] : cur;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enlargedIndex, pages]);

  const registerRef = (i, el) => {
    iframeRefs.current[i] = el;
  };

  const captureOne = async (iframe) => {
    // Wait a tick so the iframe's content fully lays out (layout + paint +
    // image decode). Without this, large data-url WebP backgrounds may still
    // be decoding when we snapshot → darker / partially rendered output.
    await new Promise((r) => setTimeout(r, 400));
    const doc = iframe.contentDocument;
    if (!doc || !doc.body) throw new Error('iframe 尚未載入');
    try {
      if (doc.fonts && doc.fonts.ready) await doc.fonts.ready;
    } catch (e) { /* ignore */ }

    // Force-decode all inline data-url images before snapshot to avoid
    // partial-decode darkness artifacts on Nano Banana base64 WebP.
    try {
      const imgs = Array.from(doc.images || []);
      await Promise.all(
        imgs.map((img) => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise((res) => {
            img.onload = img.onerror = () => res();
            // retrigger load for already-queued images
            if (img.src) img.src = img.src;
            setTimeout(res, 3000); // hard timeout guard
          });
        })
      );
    } catch (e) { /* ignore */ }

    const target = doc.documentElement;

    // Primary: html-to-image (uses SVG foreignObject + browser-native rendering,
    // so gradient compositing, color profiles, and backdrop-filter match the
    // browser preview 1:1 — fixes the "偏暗" artifact that html2canvas produces
    // on our gradient-overlaid pages).
    try {
      const blob = await htmlToImageBlob(target, {
        width: SLIDE_W,
        height: SLIDE_H,
        pixelRatio: 1,
        quality: 1,
        cacheBust: false,
        backgroundColor: '#ffffff',
        style: {
          transform: 'none',
          transformOrigin: 'top left',
          margin: '0',
          padding: '0',
        },
      });
      if (blob && blob.size > 1024) return blob;
    } catch (e) {
      // fall through to html2canvas fallback
      // eslint-disable-next-line no-console
      console.warn('html-to-image failed, falling back to html2canvas:', e);
    }

    // Fallback: html2canvas (manual CSS re-render — less color-accurate but
    // more robust for edge cases like complex SVG / web fonts).
    const canvas = await html2canvas(target, {
      width: SLIDE_W,
      height: SLIDE_H,
      windowWidth: SLIDE_W,
      windowHeight: SLIDE_H,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      imageTimeout: 15000,
      logging: false,
      scale: 1,
    });
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('canvas toBlob failed'))),
        'image/png'
      );
    });
  };

  const downloadSinglePage = async () => {
    if (!enlargedPage || !carouselId) return;
    setDownloadingPng(true);
    try {
      // Server-side render via Playwright — avoids html-to-image hang on
      // production caused by base64 WebP + iframe SVG foreignObject quirks.
      const r = await axios.post(
        `${API}/api/carousel/${carouselId}/export/page/${enlargedPage.index}`,
        {},
        { responseType: 'blob', timeout: 90000 }
      );
      const blob = r.data;
      if (!blob || blob.size < 1024) {
        throw new Error('伺服器回傳檔案太小');
      }
      const filename = `carousel_p${String(enlargedPage.index).padStart(2, '0')}_${Date.now()}.png`;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const url = URL.createObjectURL(blob);
      if (isIOS) {
        const win = window.open(url, '_blank');
        if (!win) {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        toast.success('已開啟新分頁 · 長按圖片選「儲存到相簿」');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(`已下載 第 ${enlargedPage.index} 頁 PNG`);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || '下載失敗';
      // If response is a Blob (because we set responseType blob), parse it
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          toast.error(`下載失敗：${j.detail || msg}`);
        } catch (_) {
          toast.error(`下載失敗：${msg}`);
        }
      } else {
        toast.error(`下載失敗：${msg}`);
      }
    } finally {
      setDownloadingPng(false);
    }
  };

  const downloadAll = async () => {
    // Use server-side Playwright render (prevents browser OOM on mobile).
    // Previous client-side flow (html-to-image + JSZip) peaked ~110MB RAM
    // for 7-page decks with base64 WebP backgrounds, crashing mobile tabs.
    setExporting(true);
    setProgress(0);
    let progressTimer;
    try {
      // Animate progress bar while server renders (synthetic — backend
      // doesn't stream per-page progress over REST).
      let fakeIdx = 0;
      progressTimer = setInterval(() => {
        fakeIdx = Math.min(fakeIdx + 1, pages.length);
        setProgress(fakeIdx);
      }, 1500);

      // Use axios so error/blob responses don't double-read the stream
      // (production proxy was throwing "body stream already read" via fetch).
      const r = await axios.post(
        `${API}/api/carousel/${carouselId}/export/zip`,
        {},
        { responseType: 'blob', timeout: 180000 }
      );
      clearInterval(progressTimer);
      const blob = r.data;
      if (!blob || blob.size < 200) {
        throw new Error('伺服器回傳檔案太小');
      }
      const cd = r.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `carousel_${Date.now()}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`已下載 ${pages.length} 頁 · ${(blob.size / 1024 / 1024).toFixed(1)} MB`);
      setProgress(pages.length);
    } catch (e) {
      if (progressTimer) clearInterval(progressTimer);
      // axios with responseType:blob returns error body as Blob — read once defensively
      let msg = e?.message || '下載失敗';
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const txt = await data.text();
          try {
            const j = JSON.parse(txt);
            msg = j.detail || msg;
          } catch (_) {
            if (txt) msg = txt;
          }
        } catch (_) { /* body unreadable, keep generic msg */ }
      } else if (typeof data === 'string') {
        msg = data;
      } else if (data?.detail) {
        msg = data.detail;
      }
      toast.error(`下載失敗：${String(msg).slice(0, 160)}`);
    } finally {
      setExporting(false);
      setTimeout(() => setProgress(0), 1500);
    }
  };

  return (
    <section data-testid="carousel-screen-3" className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight">
              {isBranded ? '品牌版輪播' : `最終成果 · ${pages.length} 頁`}
            </h2>
            {creditsCharged > 0 && <CreditBadge charged={creditsCharged} label="累計消耗" />}
          </div>
          <p className="atelier-caption mt-1">
            預覽為縮小呈現 · 下載時會以 1080×1350 輸出
          </p>
          {emotionalArc && (
            <p
              className="mt-4 max-w-2xl text-[12.5px] text-atelier-mute leading-[1.8] italic border-l border-atelier-coral/50 pl-3"
              data-testid="carousel-emotional-arc"
            >
              <span className="atelier-caption text-atelier-coral not-italic mr-2 tracking-wider">
                {isBranded ? 'VIBE' : 'ARC'}
              </span>
              {emotionalArc}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs text-obsidian-300 hover:text-white px-3 py-2 rounded-md border border-obsidian-600 hover:border-obsidian-400"
            data-testid="carousel-preview-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            重揀選題
          </button>
          <button
            onClick={onRestart}
            className="flex items-center gap-1.5 text-xs text-obsidian-300 hover:text-white px-3 py-2 rounded-md border border-obsidian-600 hover:border-obsidian-400"
            data-testid="carousel-preview-restart"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            重新開始
          </button>
          <button
            onClick={downloadAll}
            disabled={exporting}
            className="flex items-center gap-2 text-sm font-semibold text-obsidian-900 bg-lime-500 hover:bg-lime-400 px-4 py-2 rounded-md disabled:opacity-60"
            data-testid="carousel-download-zip"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                匯出中 {progress}/{pages.length}
              </>
            ) : (
              <>
                <Package className="w-4 h-4" />
                下載 ZIP
              </>
            )}
          </button>
          {isBranded && onSwitchClean && (
            <button
              onClick={onSwitchClean}
              className="flex items-center gap-1.5 text-xs text-obsidian-300 hover:text-white px-3 py-2 rounded-md border border-obsidian-600 hover:border-obsidian-400"
              data-testid="carousel-switch-clean"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              返乾淨版
            </button>
          )}
        </div>
      </div>

      <div className={isBranded ? '' : 'grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5'}>
        <div className="min-w-0">

      {!isBranded && (ctaDesign || hookAudit) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="carousel-audit-panel">
          {ctaDesign && (
            <div className="bg-atelier-surface border border-atelier-line rounded-xl p-4">
              <div className="atelier-caption mb-1.5 text-atelier-coral flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                CTA 策略
              </div>
              <p className="text-sm text-atelier-ink leading-relaxed">{ctaDesign}</p>
            </div>
          )}
          {hookAudit && (
            <div className="bg-atelier-surface border border-atelier-line rounded-xl p-4" data-testid="carousel-hook-audit">
              <div className="atelier-caption mb-1.5 text-atelier-coral flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  開場自我檢查
                </span>
                {hookAudit.decision && (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                      hookAudit.decision === '保留'
                        ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                        : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                    }`}
                  >
                    {hookAudit.decision}
                  </span>
                )}
              </div>
              <div className="space-y-1.5 text-[12px] text-atelier-ink">
                {hookAudit.draft_hook && (
                  <div className="flex gap-2">
                    <span className="text-atelier-subtle flex-shrink-0 w-10">初稿</span>
                    <span className="line-through text-atelier-mute">{hookAudit.draft_hook}</span>
                    {hookAudit.draft_scores?.total != null && (
                      <span className="text-atelier-subtle text-[10px] flex-shrink-0">
                        {hookAudit.draft_scores.total}/25
                      </span>
                    )}
                  </div>
                )}
                {hookAudit.final_hook && (
                  <div className="flex gap-2">
                    <span className="text-atelier-subtle flex-shrink-0 w-10">定稿</span>
                    <span className="text-atelier-ink font-medium">{hookAudit.final_hook}</span>
                    {hookAudit.final_formula && (
                      <span className="text-lime-400 text-[10px] flex-shrink-0">
                        {hookAudit.final_formula}
                      </span>
                    )}
                  </div>
                )}
                {hookAudit.change_note && (
                  <p className="text-[11px] text-atelier-mute pt-1 border-t border-atelier-line mt-2">
                    {hookAudit.change_note}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!isBranded && designRationale && (
        <div
          className="bg-atelier-surface border border-atelier-line rounded-2xl p-5 mb-6"
          data-testid="carousel-design-rationale"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-lime-400" />
            <span className="atelier-caption text-atelier-coral">
              品牌視覺設計說明
            </span>
          </div>
          {designRationale.visual_style && (
            <p className="text-sm text-atelier-ink font-medium mb-4 leading-relaxed">
              {designRationale.visual_style}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.isArray(designRationale.color_palette) && designRationale.color_palette.length > 0 && (
              <div>
                <div className="atelier-caption mb-2">
                  配色
                </div>
                <div className="space-y-1.5">
                  {designRationale.color_palette.map((c, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-[12px] text-atelier-ink">
                      <span
                        className="inline-block w-6 h-6 rounded-md border border-atelier-line flex-shrink-0"
                        style={{ backgroundColor: c?.hex || 'transparent' }}
                      />
                      <span className="font-mono text-[10px] text-atelier-subtle w-16 flex-shrink-0">
                        {c?.hex || '-'}
                      </span>
                      <span className="text-atelier-ink font-medium">{c?.name || '-'}</span>
                      {c?.usage && (
                        <span className="text-atelier-subtle text-[11px]">· {c.usage}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {designRationale.typography && (
              <div>
                <div className="atelier-caption mb-2">
                  字型
                </div>
                <p className="text-[12px] text-atelier-ink leading-relaxed">
                  {designRationale.typography}
                </p>
              </div>
            )}
          </div>
          {designRationale.reasoning && (
            <div className="mt-4 pt-3 border-t border-atelier-line">
              <div className="atelier-caption mb-1.5">
                設計邏輯
              </div>
              <p className="text-[12px] text-atelier-ink leading-relaxed italic">
                {designRationale.reasoning}
              </p>
            </div>
          )}
        </div>
      )}

      <div className={`grid grid-cols-2 ${isBranded ? 'md:grid-cols-3 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3'} gap-5`}>
        {pages.map((p) => (
          <SlidePreview
            key={p.index}
            page={p}
            index={p.index}
            registerRef={registerRef}
            totalPages={pages.length}
            onAction={!isBranded ? sendToChat : null}
            onEnlarge={setEnlargedIndex}
            onEditText={!isBranded && onEditText ? onEditText : null}
            onBgAction={!isBranded && onBgAction ? onBgAction : null}
          />
        ))}
      </div>
      </div>
      {!isBranded && (
        <div className="sticky top-4 self-start min-w-0">
          <Screen3SidePanel
            carouselId={carouselId}
            pages={pages}
            setPages={setPages}
            onRefreshUser={onRefreshUser}
            onFusionComplete={onFusionComplete}
            onAddCredits={onAddCredits}
            pendingChatPrompt={pendingChatPrompt}
          />
        </div>
      )}
      </div>

      {/* Enlarged preview modal */}
      <AnimatePresence>
        {enlargedPage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            onClick={() => setEnlargedIndex(null)}
            data-testid="carousel-enlarge-modal"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative rounded-2xl overflow-hidden border border-obsidian-500 bg-white shadow-2xl shadow-black/60"
              style={{
                width: 'min(90vw, calc((100vh - 160px) * (1080/1350)))',
                aspectRatio: `${SLIDE_W}/${SLIDE_H}`,
              }}
            >
              <iframe
                srcDoc={composeHtmlWithBg(enlargedPage.html, enlargedPage.bg_image)}
                sandbox="allow-same-origin"
                title={`slide-${enlargedPage.index}-enlarged`}
                scrolling="no"
                ref={(node) => {
                  enlargedIframeRef.current = node;
                  if (!node) return;
                  const updateScale = () => {
                    const parent = node.parentElement;
                    if (!parent) return;
                    const w = parent.clientWidth;
                    node.style.setProperty('--enlarge-scale', (w / SLIDE_W).toString());
                  };
                  updateScale();
                  // re-run on next tick to catch layout
                  setTimeout(updateScale, 0);
                }}
                style={{
                  width: `${SLIDE_W}px`,
                  height: `${SLIDE_H}px`,
                  border: 0,
                  transform: 'scale(var(--enlarge-scale))',
                  transformOrigin: 'top left',
                  backgroundColor: '#ffffff',
                }}
              />
            </motion.div>

            {/* Top-right action cluster: Download PNG + Close */}
            <div
              className="absolute top-4 right-4 flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={downloadSinglePage}
                disabled={downloadingPng}
                aria-label="下載 PNG"
                className="h-10 px-3 rounded-full bg-lime-500 text-obsidian-950 hover:bg-lime-400 disabled:bg-obsidian-700 disabled:text-obsidian-400 font-medium text-sm flex items-center gap-1.5 transition-colors shadow-lg shadow-lime-500/20"
                data-testid="carousel-enlarge-download"
              >
                {downloadingPng ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden md:inline">生成中…</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" strokeWidth={2.25} />
                    <span className="hidden md:inline">下載 PNG</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setEnlargedIndex(null)}
                aria-label="關閉"
                className="w-10 h-10 rounded-full bg-obsidian-950/80 border border-obsidian-600 text-white hover:bg-obsidian-900 flex items-center justify-center transition-colors"
                data-testid="carousel-enlarge-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Page indicator + keyboard hint */}
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-obsidian-950/80 border border-obsidian-600 rounded-full px-4 py-2 text-xs text-obsidian-200 font-mono"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  const idxs = pages.map((p) => p.index);
                  const i = idxs.indexOf(enlargedIndex);
                  if (i > 0) setEnlargedIndex(idxs[i - 1]);
                }}
                disabled={pages.findIndex((p) => p.index === enlargedIndex) === 0}
                className="hover:text-lime-300 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="上一頁"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <span>
                第 {enlargedPage.index} 頁 · {pages.findIndex((p) => p.index === enlargedIndex) + 1} / {pages.length}
              </span>
              <button
                onClick={() => {
                  const idxs = pages.map((p) => p.index);
                  const i = idxs.indexOf(enlargedIndex);
                  if (i < idxs.length - 1) setEnlargedIndex(idxs[i + 1]);
                }}
                disabled={pages.findIndex((p) => p.index === enlargedIndex) === pages.length - 1}
                className="hover:text-lime-300 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="下一頁"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
              <span className="text-obsidian-500 hidden md:inline ml-2">ESC / ← →</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Side panel: tabs [chat | fusion]
// --- Screen 3 Side Panel: Hooks Picker ------------------------------------
const HooksPicker = ({ carouselId, onApplied, onRefreshUser, onAddCredits }) => {
  const STORAGE_KEY = `carousel-hooks-cache-${carouselId}`;
  // Hydrate from localStorage so hooks survive tab switches / re-mounts
  const [hooks, setHooks] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.hooks)) return parsed.hooks;
      }
    } catch (_) { /* noop */ }
    return [];
  });
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [applying, setApplying] = useState(null);
  const [totalExcluded, setTotalExcluded] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw)?.totalExcluded || 0;
    } catch (_) { /* noop */ }
    return 0;
  });
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return !!JSON.parse(raw)?.hasGeneratedOnce;
    } catch (_) { /* noop */ }
    return false;
  });

  // Persist state to localStorage so navigation away + return restores it
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ hooks, totalExcluded, hasGeneratedOnce, savedAt: Date.now() })
      );
    } catch (_) { /* noop - quota */ }
  }, [hooks, totalExcluded, hasGeneratedOnce, STORAGE_KEY]);

  const fetchBatch = async () => {
    setLoadingBatch(true);
    try {
      const r = await axios.post(`${API}/api/carousel/${carouselId}/hooks/batch`, {
        extra_exclude: hooks.map((h) => h.hook),
      });
      setHooks(Array.isArray(r.data.hooks) ? r.data.hooks : []);
      setTotalExcluded(r.data.excluded_count || 0);
      setHasGeneratedOnce(true);
      if (onRefreshUser) onRefreshUser();
      const charged = r.data.credits_charged;
      if (typeof charged === 'number' && charged > 0) {
        if (onAddCredits) onAddCredits(charged);
        toast.success(`生成 10 條新開場文案 · 扣除 ${charged.toFixed(1)} 點數`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || '生成失敗';
      toast.error(String(msg).slice(0, 120));
    } finally {
      setLoadingBatch(false);
    }
  };

  const applyHook = async (h) => {
    setApplying(h.hook);
    try {
      const r = await axios.post(`${API}/api/carousel/${carouselId}/hooks/apply`, {
        chosen_hook: h.hook,
        formula: h.formula || null,
        layout: h.layout || null,
      });
      if (r.data?.p1_html) {
        onApplied(r.data.p1_html);
        if (onRefreshUser) onRefreshUser();
        const charged = r.data.credits_charged;
        if (typeof charged === 'number' && charged > 0 && onAddCredits) onAddCredits(charged);
        toast.success(
          charged > 0
            ? `封面已換成「${h.hook}」· 扣除 ${charged.toFixed(1)} 點數`
            : `封面已換成「${h.hook}」`
        );
        // Remove the chosen one from visible list so user can keep picking without re-generating
        setHooks((prev) => prev.filter((x) => x.hook !== h.hook));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || '套用失敗';
      toast.error(String(msg).slice(0, 120));
    } finally {
      setApplying(null);
    }
  };

  return (
    <div
      className="atelier-card p-4 h-[calc(100vh-14rem)] max-h-[640px] overflow-y-auto"
      data-testid="carousel-hooks-picker"
    >
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-3.5 h-3.5 text-atelier-coral" strokeWidth={1.6} />
        <span className="atelier-caption text-atelier-coral">開場實驗室</span>
        <span className="h-px flex-1 bg-atelier-line" />
      </div>
      <p className="text-[13px] text-atelier-mute mb-3 leading-[1.7]">
        用同一套上下文生成 10 條全新的封面開場文案。選一條後，AI 只會重寫第 1 頁封面，
        <span className="text-atelier-ink font-medium">其他頁面保持不變</span>。
      </p>
      {hasGeneratedOnce && totalExcluded > 0 && (
        <div className="atelier-caption mb-3 tabular-nums">
          已排除 <span className="text-atelier-coral">{totalExcluded}</span> 條舊開場文案 · 不會重複
        </div>
      )}

      {!hasGeneratedOnce && !loadingBatch && (
        <button
          onClick={fetchBatch}
          className="w-full atelier-btn-primary atelier-glow-coral h-12 rounded-full"
          data-testid="carousel-hooks-generate-btn"
        >
          <Sparkles className="w-4 h-4" strokeWidth={1.8} />
          生成 10 條開場文案
        </button>
      )}

      {loadingBatch && (
        <div className="flex items-center justify-center gap-3 py-8 text-atelier-ink text-sm">
          <div className="atelier-oil-loader" style={{ width: 22, height: 22 }} />
          <span>AI 正在生成 10 條新的開場文案…</span>
        </div>
      )}

      {!loadingBatch && hooks.length > 0 && (
        <div className="space-y-2.5 atelier-stagger">
          {hooks.map((h, idx) => (
            <div
              key={`${h.hook}-${idx}`}
              className="atelier-card p-3.5 hover:border-atelier-coral/50 transition-all duration-300"
              data-testid={`carousel-hook-item-${idx}`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {h.formula && (
                  <span className="text-[10px] font-mono text-atelier-coral bg-atelier-coral/[0.08] border border-atelier-coral/40 px-2 py-0.5 rounded-full">
                    {h.formula}
                  </span>
                )}
                {h.category && (
                  <span className="atelier-caption">{h.category}</span>
                )}
                {typeof h.tension_count === 'number' && (
                  <span className="atelier-caption ml-auto tabular-nums">
                    張力 {h.tension_count}/7
                  </span>
                )}
                {typeof h.score === 'number' && (
                  <span className="atelier-caption tabular-nums">
                    {h.score}/25
                  </span>
                )}
              </div>
              <div
                className="text-atelier-ink text-[15px] leading-[1.55] mb-2 whitespace-pre-line tracking-tight"
                style={{ fontFamily: "'Noto Serif TC', Georgia, serif", fontWeight: 500 }}
              >
                {h.hook}
              </div>
              {h.why && (
                <div className="text-[12px] text-atelier-mute leading-[1.75] mb-2.5 italic border-l border-atelier-line pl-2.5">
                  {h.why}
                </div>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-atelier-line">
                {h.layout && (
                  <span className="text-[10px] font-mono text-atelier-subtle">
                    {h.layout}
                  </span>
                )}
                <button
                  onClick={() => applyHook(h)}
                  disabled={applying !== null}
                  className={`ml-auto inline-flex items-center gap-1.5 text-[11px] h-7 px-3 rounded-full transition-all duration-300 ${
                    applying === h.hook
                      ? 'bg-atelier-coral/20 border border-atelier-coral/40 text-atelier-coral'
                      : 'bg-atelier-coral text-white hover:bg-atelier-coralHi hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100'
                  }`}
                  style={{ fontWeight: 500 }}
                  data-testid={`carousel-hook-apply-${idx}`}
                >
                  {applying === h.hook ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" /> 重寫中…
                    </>
                  ) : (
                    <>
                      用呢個 <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={fetchBatch}
            disabled={loadingBatch || applying !== null}
            className="w-full mt-3 h-11 rounded-full border border-atelier-coral/40 text-atelier-coral hover:bg-atelier-coral/[0.06] hover:border-atelier-coral disabled:opacity-40 disabled:cursor-not-allowed text-sm transition-all duration-300 flex items-center justify-center gap-1.5"
            style={{ fontWeight: 500 }}
            data-testid="carousel-hooks-regen-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} />
            換一批（排除以上 {hooks.length} 條）
          </button>
        </div>
      )}

      {!loadingBatch && hasGeneratedOnce && hooks.length === 0 && (
        <div className="text-center py-6 text-atelier-mute text-sm">
          所有開場文案都已使用或被排除
          <button
            onClick={fetchBatch}
            className="block mx-auto mt-3 text-atelier-coral hover:text-atelier-coralHi text-sm underline decoration-dotted underline-offset-4"
          >
            再生成 10 條
          </button>
        </div>
      )}
    </div>
  );
};


const Screen3SidePanel = ({ carouselId, pages, setPages, onRefreshUser, onFusionComplete, onAddCredits, pendingChatPrompt }) => {
  const [tab, setTab] = useState('chat'); // 'chat' | 'fusion' | 'hooks'
  // Auto-switch to chat tab when a page action is clicked
  useEffect(() => {
    if (pendingChatPrompt) setTab('chat');
  }, [pendingChatPrompt]);
  const handlePageEdit = (index, html, bgImage) => {
    // bgImage is populated when a GEN_IMAGE or a subsequent EDIT on a
    // decoupled-bg page fires.  Keeping it separate from `html` in state
    // means the AI never sees the base64 image on any future request → the
    // "圖片同 HTML 融為一體" bug is structurally impossible.
    setPages((prev) =>
      prev.map((p) => {
        if (p.index !== index) return p;
        const next = { ...p, html };
        if (bgImage !== undefined) next.bg_image = bgImage;
        return next;
      })
    );
  };
  // Incoming new page from SSE (ADD_PAGE)
  const handlePageAdded = (newPage, afterIndex) => {
    setPages((prev) => {
      // Shift indices of subsequent pages
      const shifted = prev.map((p) =>
        p.index > afterIndex ? { ...p, index: p.index + 1 } : p
      );
      const insertIdx = shifted.findIndex((p) => p.index > afterIndex);
      const insertedAt = insertIdx === -1 ? shifted.length : insertIdx;
      return [
        ...shifted.slice(0, insertedAt),
        { index: afterIndex + 1, html: newPage.html },
        ...shifted.slice(insertedAt),
      ].sort((a, b) => a.index - b.index);
    });
  };
  // Incoming deleted page
  const handlePageDeleted = (index) => {
    setPages((prev) =>
      prev
        .filter((p) => p.index !== index)
        .map((p) => (p.index > index ? { ...p, index: p.index - 1 } : p))
        .sort((a, b) => a.index - b.index)
    );
  };
  // Incoming reordered pages (full new order: [{index, html}])
  const handlePageReordered = (newPages) => {
    setPages(newPages.slice().sort((a, b) => a.index - b.index));
  };

  return (
    <div data-testid="carousel-screen3-side" className="flex flex-col">
      <div className="flex atelier-card p-1 mb-3 gap-1">
        {[
          { id: 'chat',   icon: Sparkles, label: '對話編輯', testid: 'carousel-screen3-tab-chat' },
          { id: 'hooks',  icon: Zap,      label: '換 Hook',  testid: 'carousel-screen3-tab-hooks' },
          { id: 'fusion', icon: Wand2,    label: '生成底圖', testid: 'carousel-screen3-tab-fusion' },
        ].map(({ id, icon: Icon, label, testid }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 h-9 inline-flex items-center justify-center gap-1.5 rounded-full transition-all duration-300 ${
                active
                  ? 'bg-atelier-coral text-white'
                  : 'text-atelier-mute hover:text-atelier-ink hover:bg-atelier-surface2'
              }`}
              style={{
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: "'Noto Sans TC', 'Inter', -apple-system, sans-serif",
                letterSpacing: '-0.01em',
              }}
              data-testid={testid}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={active ? 2 : 1.6} />
              <span>{label}</span>
            </button>
          );
        })}
      </div>

      {tab === 'chat' && (
        <CarouselPagesChat
          carouselId={carouselId}
          pages={pages}
          onPageEdit={handlePageEdit}
          onPageAdded={handlePageAdded}
          onPageDeleted={handlePageDeleted}
          onPageReordered={handlePageReordered}
          onRefreshUser={onRefreshUser}
          onAddCredits={onAddCredits}
          pendingPrompt={pendingChatPrompt}
        />
      )}
      {tab === 'hooks' && (
        <HooksPicker
          carouselId={carouselId}
          onApplied={(p1Html) => handlePageEdit(1, p1Html)}
          onRefreshUser={onRefreshUser}
          onAddCredits={onAddCredits}
        />
      )}
      {tab === 'fusion' && (
        <div className="atelier-card overflow-hidden">
          <FusionUploadScreen
            carouselId={carouselId}
            onCancel={() => setTab('chat')}
            onComplete={(job) => {
              if (onFusionComplete) onFusionComplete(job);
              if (typeof job.credits_charged === 'number' && job.credits_charged > 0) {
                if (onAddCredits) onAddCredits(job.credits_charged);
                toast.success(`底圖生成扣除 ${job.credits_charged.toFixed(1)} 點數`);
              }
              // After fusion, bring the user back to the chat tab so they can
              // continue iterating on the newly branded pages.
              setTab('chat');
            }}
          />
        </div>
      )}
    </div>
  );
};

// --- Main Component -------------------------------------------------------
// --- Confirmation dialog before committing to Prompt B generation ---------
const ConfirmTopicDialog = ({ topic, remainingCredits, onConfirm, onCancel }) => {
  if (!topic) return null;
  const { low, high } = GENERATE_COST_ESTIMATE;
  const affordable = (remainingCredits ?? Infinity) >= high;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="carousel-confirm-topic-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-obsidian-600 bg-obsidian-950 shadow-2xl p-6"
      >
        <div className="flex items-center gap-2 text-lime-400 mb-3">
          <Coins className="w-4 h-4" />
          <span className="label-micro">COST PREVIEW · 確認生成</span>
        </div>
        <h3 className="font-display text-white text-xl leading-snug mb-2 line-clamp-2">
          {topic.title}
        </h3>
        <p className="text-[12px] text-atelier-mute leading-relaxed mb-5">
          AI 即將為你生成 7 頁輪播貼文（包括文案設計、版面排版及開場檢查），通常需要 1 至 3 分鐘。
          <br />
          <span className="text-atelier-coral">✨ 生成後可以逐頁直接編輯文字，即時預覽效果</span>
        </p>
        <div className="bg-obsidian-900/70 border border-obsidian-700 rounded-xl p-4 mb-5">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[11px] text-obsidian-400">估算消耗</span>
            <span className="font-mono text-white text-lg tabular-nums">
              ≈ {low}–{high} credits
            </span>
          </div>
          {typeof remainingCredits === 'number' && (
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] text-obsidian-400">可用餘額</span>
              <span className={`font-mono tabular-nums text-sm ${affordable ? 'text-obsidian-200' : 'text-red-400'}`}>
                {remainingCredits.toLocaleString('en-US', { maximumFractionDigits: 0 })} 點數
              </span>
            </div>
          )}
        </div>
        {!affordable && (
          <div className="flex items-start gap-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">
            <span>餘額可能不足，建議先去 Profile 充值。</span>
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 h-10 rounded-lg border border-obsidian-600 text-obsidian-200 hover:border-obsidian-500 text-sm font-medium transition-colors"
            data-testid="carousel-confirm-cancel"
          >
            再諗下
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 h-10 rounded-lg bg-lime-500 hover:bg-lime-400 text-obsidian-950 text-sm font-semibold transition-colors"
            data-testid="carousel-confirm-generate"
          >
            確認生成
          </button>
        </div>
      </div>
    </div>
  );
};


const Carousel = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => ({
    ...DEFAULT_FORM,
    ...(loadLastForm() || {}),
    topic: '', // never restore topic — each project is a new topic
  }));

  useEffect(() => {
    saveLastForm(form);
  }, [form]);
  const [topics, setTopics] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [topicsCredits, setTopicsCredits] = useState(0);
  const [pages, setPages] = useState([]);
  const [emotionalArc, setEmotionalArc] = useState('');
  const [ctaDesign, setCtaDesign] = useState('');
  const [hookAudit, setHookAudit] = useState(null);
  const [pagesCredits, setPagesCredits] = useState(0);
  const [loadingPages, setLoadingPages] = useState(false);
  const [stage, setStage] = useState('');
  // Multi-stage progress for Screen 3 generation (pure FE simulation since Opus call is monolithic)
  const [stageIdx, setStageIdx] = useState(0);
  // Confirm dialog before actual generation — shows estimated credits
  const [pendingTopic, setPendingTopic] = useState(null); // Topic object waiting for confirmation
  const [inlineEditPage, setInlineEditPage] = useState(null); // C2 · inline text edit modal page ref
  // Feature 2 · manual bg image menu — {page, rect} shape, null when closed
  const [bgMenuPage, setBgMenuPage] = useState(null);
  const [bgMenuAnchorRect, setBgMenuAnchorRect] = useState(null);

  // Advance progress stages during loadingPages — purely FE simulation.
  // Each stage advances after its etaSec elapses. Caps at last stage if generation
  // takes longer than total (Opus still working, just keep showing "最終品質審查中").
  useEffect(() => {
    if (!loadingPages) return undefined;
    setStageIdx(0);
    setStage(GENERATION_STAGES[0].label);
    let cancelled = false;
    let timer = null;
    const advance = (idx) => {
      if (cancelled) return;
      if (idx >= GENERATION_STAGES.length) return;
      setStageIdx(idx);
      setStage(GENERATION_STAGES[idx].label);
      const eta = GENERATION_STAGES[idx].etaSec * 1000;
      timer = setTimeout(() => advance(idx + 1), eta);
    };
    advance(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [loadingPages]);
  const [cleanCarouselId, setCleanCarouselId] = useState(null);
  const [brandedPages, setBrandedPages] = useState(null);
  const [brandedArc, setBrandedArc] = useState('');
  const [brandedCarouselId, setBrandedCarouselId] = useState(null);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenHistory, setRegenHistory] = useState([]); // chat turns within modal
  const [regenStreamingText, setRegenStreamingText] = useState(''); // live clarify buffer
  const [regenSending, setRegenSending] = useState(false);
  const [regenCredits, setRegenCredits] = useState(0);
  const [regenSuggestions, setRegenSuggestions] = useState([]);
  const [regenSuggestionsLoading, setRegenSuggestionsLoading] = useState(false);
  const [designRationale, setDesignRationale] = useState(null);
  const [seenTitles, setSeenTitles] = useState([]); // session-level dedup list

  const streamTopics = async () => {
    setStreaming(true);
    setTopics([]);
    setTopicsCredits(0);
    setStep(2);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/carousel/topics/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          detail = j.detail || detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n\n');
        sseBuffer = lines.pop() || '';

        for (const chunk of lines) {
          const trimmed = chunk.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload) continue;
          let evt;
          try {
            evt = JSON.parse(payload);
          } catch (e) {
            continue;
          }
          if (evt.type === 'topic' && evt.data) {
            setTopics((prev) => [...prev, evt.data]);
            if (evt.data?.title) {
              setSeenTitles((prev) => Array.from(new Set([...prev, evt.data.title])));
            }
          } else if (evt.type === 'done') {
            setTopicsCredits(evt.credits_charged || 0);
            refreshUser();
            toast.success(
              `已生成 ${evt.count} 個選題 · 消耗 ${(evt.credits_charged || 0).toFixed(2)} 點數`
            );
          } else if (evt.type === 'error') {
            throw new Error(evt.message || 'stream error');
          }
        }
      }
    } catch (e) {
      toast.error(`選題生成失敗：${e.message}`);
      setStep(1);
    } finally {
      setStreaming(false);
    }
  };

  // Fresh restart of Screen 1 form — clears seen titles to start a new session
  const restartForm = () => {
    setSeenTitles([]);
    setStep(1);
  };

  // Allow navigating back to the 揀選題 step unconditionally. If topics are
  // in memory (normal flow or openProject restored from topic_candidates),
  // user sees the 10 cards. Otherwise Screen2Topics renders an empty-state
  // CTA (backfill for older decks saved before persistence).
  const backToTopics = () => {
    setStep(2);
  };

  // Supplement regen: AI either clarifies in-modal OR streams new topics progressively
  const submitRegenSupplement = async (supplementText) => {
    setRegenSending(true);
    setRegenStreamingText('');
    const nextHistory = [...regenHistory, { role: 'user', content: supplementText }];
    setRegenHistory(nextHistory);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/carousel/topics/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          form,
          topics,
          history: regenHistory.map((m) => ({ role: m.role, content: m.content })),
          message: supplementText,
          excluded_titles: seenTitles,
        }),
      });

      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          detail = j.detail || detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let sseBuf = '';
      let regenMode = false;
      let clarifyBuf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, { stream: true });
        const chunks = sseBuf.split('\n\n');
        sseBuf = chunks.pop() || '';
        for (const c of chunks) {
          const line = c.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let evt;
          try {
            evt = JSON.parse(payload);
          } catch (_) {
            continue;
          }
          if (evt.type === 'token' && evt.text) {
            if (regenMode) continue;
            clarifyBuf += evt.text;
            setRegenStreamingText(clarifyBuf);
          } else if (evt.type === 'regen_start') {
            // AI decided to regenerate → close modal and switch to Screen 2 streaming UI
            regenMode = true;
            setRegenOpen(false);
            setRegenHistory([]);
            setRegenStreamingText('');
            setTopics([]);
            setStreaming(true);
            setTopicsCredits(0);
            setStep(2);
          } else if (evt.type === 'topic' && evt.data) {
            // Progressive topic arrival during regen
            setTopics((prev) => [...prev, evt.data]);
            if (evt.data?.title) {
              setSeenTitles((prev) => Array.from(new Set([...prev, evt.data.title])));
            }
          } else if (evt.type === 'done') {
            setRegenCredits((c) => c + (evt.credits_charged || 0));
            if (evt.regenerated) {
              setStreaming(false);
              setTopicsCredits(evt.credits_charged || 0);
              toast.success(`已重新生成 ${evt.regen_count || 0} 題選題`);
              refreshUser();
            } else {
              // Clarifying reply — stays in modal
              const reply = (evt.full_text || clarifyBuf || '').trim();
              setRegenHistory([...nextHistory, { role: 'assistant', content: reply }]);
              setRegenStreamingText('');
            }
          } else if (evt.type === 'error') {
            throw new Error(evt.message || 'stream error');
          }
        }
      }
    } catch (e) {
      if (regenOpen) {
        setRegenStreamingText(`⚠️ ${e.message}`);
      } else {
        setStreaming(false);
      }
      toast.error(`重新生成失敗：${e.message}`);
    } finally {
      setRegenSending(false);
    }
  };

  const openRegen = async () => {
    setRegenHistory([]);
    setRegenStreamingText('');
    setRegenCredits(0);
    setRegenSending(false);
    setRegenOpen(true);
    // fetch personalized suggestions (non-blocking — modal uses defaults while loading)
    setRegenSuggestions([]);
    setRegenSuggestionsLoading(true);
    try {
      const r = await axios.post(`${API}/api/carousel/topics/regen-suggestions`, {
        form,
        topics,
        excluded_titles: seenTitles,
      });
      if (Array.isArray(r.data?.suggestions) && r.data.suggestions.length > 0) {
        setRegenSuggestions(r.data.suggestions);
      }
    } catch (_) { /* silent — modal will fall back to generic presets */ }
    finally {
      setRegenSuggestionsLoading(false);
    }
  };

  // Stage 1 of pick: show confirmation dialog (don't start API yet)
  const pickTopic = (t) => {
    setPendingTopic(t);
  };

  // Stage 2: user confirmed → actually run the generation.
  const confirmAndGenerate = async () => {
    const t = pendingTopic;
    if (!t) return;
    setPendingTopic(null);
    setLoadingPages(true);
    setStage(GENERATION_STAGES[0]);
    setStageIdx(0);
    setPagesCredits(0);
    setStep(3);
    try {
      const r = await axios.post(`${API}/api/carousel/generate`, {
        title: t.title,
        arc_direction: t.arc_direction,
        ip_positioning: form.ip_positioning,
        target_audience: form.target_audience,
        instagram_handle: form.instagram_handle,
        key_points: t.key_points,
        funnel: form.funnel,
        language: form.language,
        language_style: form.language_style,
        // Forward Prompt A metadata so HOOKS / COVER_REWRITE have full topic context
        dna: t.dna,
        arc: t.arc,
        hook_preview: t.hook_preview,
        hook_formula: t.hook_formula,
        hook_tension: t.hook_tension,
        hook_layout: t.hook_layout,
        // 10 候選 topics — 持久化到 DB，令用戶之後再 open deck 時可以返「揀選題」step 睇返。
        all_topics: topics,
      });
      const jobId = r.data.job_id;
      if (!jobId) throw new Error('後端未回傳 job_id');
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const jr = await axios.get(`${API}/api/carousel/job/${jobId}`);
          const job = jr.data;
          if (job.status === 'done') {
            clearInterval(poll);
            setPages(job.pages || []);
            setEmotionalArc(job.emotional_arc || '');
            setCtaDesign(job.cta_design || '');
            setHookAudit(job.hook_audit || null);
            setDesignRationale(job.design_rationale || null);
            setPagesCredits(job.credits_charged || 0);
            setCleanCarouselId(job.carousel_id || null);
            setLoadingPages(false);
            setStage('');
            refreshUser();
            toast.success(
              `${(job.pages || []).length} 頁生成完成 · 消耗 ${(job.credits_charged || 0).toFixed(2)} 點數`
            );
          } else if (job.status === 'failed') {
            clearInterval(poll);
            setLoadingPages(false);
            setStage('');
            toast.error(`生成失敗：${job.error || '未知錯誤'}`);
            setStep(2);
          }
        } catch (err) {
          if (err.response?.status === 404) {
            clearInterval(poll);
            setLoadingPages(false);
            toast.error('任務不存在');
            setStep(2);
          }
        }
        if (attempts > 120) {
          clearInterval(poll);
          setLoadingPages(false);
          toast.error('超時（超過 4 分鐘），請再試');
          setStep(2);
        }
      }, 2000);
    } catch (e) {
      setLoadingPages(false);
      setStage('');
      toast.error(e.response?.data?.detail || e.message);
      setStep(2);
    }
  };

  const restart = () => {
    setStep(1);
    setTopics([]);
    setTopicsCredits(0);
    setPages([]);
    setEmotionalArc('');
    setCtaDesign('');
    setHookAudit(null);
    setDesignRationale(null);
    setPagesCredits(0);
    setCleanCarouselId(null);
    setBrandedPages(null);
    setBrandedArc('');
    setBrandedCarouselId(null);
  };

  const onFusionComplete = (job) => {
    // Stay on step 3 (最終成果). Replace current pages with the branded ones and
    // repoint cleanCarouselId to the branded carousel so subsequent chat edits
    // save to the correct DB doc. The original clean version remains in DB and
    // can be reopened via 我的專案 drawer.
    const brandedPages = job.pages || [];
    const brandedId = job.branded_carousel_id || null;
    if (brandedPages.length > 0) {
      setPages(brandedPages);
    }
    if (job.vibe_brief) {
      setEmotionalArc(job.vibe_brief);
    }
    if (brandedId) {
      setCleanCarouselId(brandedId);
    }
    // Clear any legacy branded state to keep a single source of truth on Screen 3
    setBrandedPages(null);
    setBrandedArc('');
    setBrandedCarouselId(null);
    refreshUser();
    toast.success('底圖生成完成 · 已載入到編輯器');
    // remain on step 3 (no setStep call)
  };

  const loadProjects = async () => {
    try {
      const r = await axios.get(`${API}/api/carousel/list`);
      setProjects(r.data || []);
    } catch (e) {
      toast.error('載入專案列表失敗');
    }
  };

  const openProject = async (id, variant) => {
    try {
      const r = await axios.get(`${API}/api/carousel/${id}`);
      const doc = r.data;
      // Unified Screen 3 editor: ALL projects (clean or branded) open on step 3.
      // Branded projects simply load their own pages; user can continue editing or
      // regenerate backgrounds via the 生成底圖 tab.
      setPages(doc.pages || []);
      setEmotionalArc(doc.emotional_arc || doc.vibe_brief || '');
      setCtaDesign(doc.cta_design || '');
      setHookAudit(doc.hook_audit || null);
      setDesignRationale(doc.design_rationale || null);
      setCleanCarouselId(doc.id);
      // Reset session-cumulative credits counter so badge shows only what the
      // user spends FROM THIS POINT forward (each session is a fresh counter).
      setPagesCredits(0);
      // Restore topic candidates (so user can navigate back to 揀選題 step)
      // Backfill: older decks saved before the topic_candidates field was added
      // simply have an empty array — the Stepper then keeps step 2 disabled.
      setTopics(Array.isArray(doc.topic_candidates) ? doc.topic_candidates : []);
      setTopicsCredits(0);
      setSeenTitles([]);
      // Restore form fields so user can also navigate back to step 1 and
      // re-generate topics / other decks using the same IP/audience settings.
      setForm((f) => ({
        ...f,
        topic: doc.title || f.topic,
        ip_positioning: doc.ip_positioning || f.ip_positioning,
        target_audience: doc.target_audience || f.target_audience,
        instagram_handle: doc.instagram_handle || f.instagram_handle,
        funnel: doc.funnel || f.funnel,
        language: doc.language || f.language,
        language_style: doc.language_style || f.language_style,
      }));
      // Clear legacy branded state
      setBrandedPages(null);
      setBrandedArc('');
      setBrandedCarouselId(null);
      setStep(3);
      setProjectsOpen(false);
      // Silence unused-var lint
      void variant;
    } catch (e) {
      toast.error('讀取專案失敗');
    }
  };

  useEffect(() => {
    if (projectsOpen) loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsOpen]);

  // Poll list while drawer is open AND there are in-progress jobs, so the UI
  // auto-updates when a generation finishes (removing the spinner + promoting
  // it to a clickable completed project).
  useEffect(() => {
    if (!projectsOpen) return undefined;
    const hasGenerating = projects.some((p) => p._generating);
    if (!hasGenerating) return undefined;
    const t = setInterval(() => { loadProjects(); }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsOpen, projects]);

  // Auto-open project from ?open=<id>&variant=<clean|branded> URL param (for /carousel/history deep-link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (!openId) return;
    const variant = params.get('variant') || 'clean';
    (async () => {
      await openProject(openId, variant);
      // Clean the URL so refresh doesn't re-trigger load of the same project
      window.history.replaceState({}, '', '/carousel');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-theme="dark" data-atelier="true" className="flex min-h-screen surface-obsidian text-white">
      <MainNavigation />

      <main className="flex-1 overflow-y-auto py-8 md:py-12 px-4 md:px-8 pb-24 md:pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-lime-500" />
              <span className="label-micro text-lime-500">CAROUSEL · ATELIER</span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <span
                data-testid="carousel-credits-badge"
                className="flex items-center gap-1.5 text-xs font-semibold text-lime-300 bg-lime-500/10 border border-lime-500/30 px-3 py-1.5 rounded-md"
                title="剩餘 Credits"
              >
                <Sparkles className="w-3 h-3" />
                {Number(user?.credits ?? 0).toFixed(2)} 點數
              </span>
              <button
                onClick={() => setProjectsOpen(true)}
                className="flex items-center gap-1.5 text-xs text-obsidian-300 hover:text-white px-3 py-1.5 rounded-md border border-obsidian-600 hover:border-obsidian-400"
                data-testid="carousel-projects-btn"
              >
                <FolderOpen className="w-3.5 h-3.5" />
                我的專案
              </button>
              <button
                onClick={restart}
                className="flex items-center gap-1.5 text-xs text-obsidian-900 font-semibold bg-lime-500 hover:bg-lime-400 px-3 py-1.5 rounded-md"
                data-testid="carousel-new-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                新專案
              </button>
            </div>
          </div>

          <Stepper
            step={step}
            topicsCount={topics.length}
            pagesCount={pages.length}
            onNavigate={(target) => {
              if (target === 1) {
                setStep(1);
              } else if (target === 2) {
                backToTopics();
              } else if (target === 3) {
                if (pages.length > 0) setStep(3);
              }
            }}
          />

          {step === 1 && (
            <Screen1Input
              form={form}
              setForm={setForm}
              onNext={streamTopics}
              loading={streaming}
            />
          )}

          {step === 2 && (
            <Screen2Topics
              topics={topics}
              streaming={streaming}
              creditsCharged={topicsCredits}
              onPick={pickTopic}
              onBack={restartForm}
              onOpenRegen={openRegen}
              onFreshGenerate={streamTopics}
            />
          )}

          {step === 3 && loadingPages && (
            <div className="max-w-xl mx-auto py-12" data-testid="carousel-loading">
              <div className="text-center mb-8">
                <Loader2 className="w-10 h-10 animate-spin text-lime-500 mx-auto mb-5" />
                <h3 className="font-display text-xl text-white mb-1">
                  AI 正為你生成 7 頁輪播貼文
                </h3>
                <p className="text-[11px] text-obsidian-500">
                  整個流程通常 1 至 3 分鐘 · 請耐心等候
                </p>
              </div>
              <ol className="space-y-2.5 bg-obsidian-950/60 border border-obsidian-700 rounded-xl p-4">
                {GENERATION_STAGES.map((s, i) => {
                  const done = i < stageIdx;
                  const active = i === stageIdx;
                  return (
                    <li
                      key={s.label}
                      data-testid={`carousel-stage-${i}`}
                      className={`flex items-center gap-2.5 text-sm transition-colors ${
                        active ? 'text-lime-400' : done ? 'text-obsidian-300' : 'text-obsidian-600'
                      }`}
                    >
                      {done ? (
                        <Check className="w-4 h-4 text-lime-500 flex-shrink-0" />
                      ) : active ? (
                        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                      ) : (
                        <span className="w-4 h-4 rounded-full border border-obsidian-700 flex-shrink-0" />
                      )}
                      <span className={active ? 'font-medium' : ''}>{s.label}</span>
                      {active && <span className="ml-auto text-[10px] text-obsidian-500 font-mono">正在處理…</span>}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {step === 3 && !loadingPages && pages.length > 0 && (
            <Screen3Preview
              pages={pages}
              setPages={setPages}
              carouselId={cleanCarouselId}
              emotionalArc={emotionalArc}
              ctaDesign={ctaDesign}
              hookAudit={hookAudit}
              designRationale={designRationale}
              creditsCharged={pagesCredits}
              isBranded={false}
              onBack={backToTopics}
              onRestart={restart}
              onRefreshUser={refreshUser}
              onFusionComplete={onFusionComplete}
              onAddCredits={(amt) => setPagesCredits((c) => c + (amt || 0))}
              onEditText={(p) => setInlineEditPage(p)}
              onBgAction={(p, rect) => {
                setBgMenuPage(p);
                setBgMenuAnchorRect(rect);
              }}
            />
          )}

          {/* Legacy step 4 (standalone FusionUploadScreen) — only shown if user opens fusion via a saved branded project */}
          {step === 4 && cleanCarouselId && (
            <FusionUploadScreen
              carouselId={cleanCarouselId}
              onCancel={() => setStep(3)}
              onComplete={onFusionComplete}
            />
          )}
        </div>
      </main>

      <ConfirmTopicDialog
        topic={pendingTopic}
        remainingCredits={user?.credits}
        onConfirm={confirmAndGenerate}
        onCancel={() => setPendingTopic(null)}
      />

      <InlineTextEditModal
        open={!!inlineEditPage}
        carouselId={cleanCarouselId}
        page={inlineEditPage}
        onClose={() => setInlineEditPage(null)}
        onPageUpdate={(index, html) => {
          setPages((prev) =>
            prev.map((p) => (p.index === index ? { ...p, html } : p)),
          );
          // Keep the modal's page snapshot in sync so subsequent parse-and-swap
          // uses the freshest HTML (idempotent — same html string).
          setInlineEditPage((prev) => (prev && prev.index === index ? { ...prev, html } : prev));
        }}
      />

      <BgImageMenu
        open={!!bgMenuPage}
        onClose={() => {
          setBgMenuPage(null);
          setBgMenuAnchorRect(null);
        }}
        carouselId={cleanCarouselId}
        page={bgMenuPage}
        anchorRect={bgMenuAnchorRect}
        onPageUpdate={(index, patch) => {
          setPages((prev) =>
            prev.map((p) => (p.index === index ? { ...p, ...patch } : p)),
          );
        }}
        onCreditsCharged={() => refreshUser()}
      />

      {projectsOpen && (
        <ProjectsDrawer
          projects={projects}
          onClose={() => setProjectsOpen(false)}
          onOpen={openProject}
        />
      )}

      <TopicsRegenerateModal
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        history={regenHistory}
        streamingText={regenStreamingText}
        sending={regenSending}
        credits={regenCredits}
        suggestions={regenSuggestions}
        suggestionsLoading={regenSuggestionsLoading}
        onSubmit={submitRegenSupplement}
        onDirectRegenerate={streamTopics}
      />
    </div>
  );
};

// -------------------------- Projects Drawer --------------------------------
const ProjectsDrawer = ({ projects, onClose, onOpen }) => {
  // Flat list by date (newest first). Generating jobs bubble to the top.
  const sorted = [...projects].sort((a, b) => {
    // in-progress first, then newest-first by created_at
    if (a._generating && !b._generating) return -1;
    if (b._generating && !a._generating) return 1;
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="carousel-projects-drawer">
      <div className="flex-1 bg-atelier-ink/60 backdrop-blur-sm" onClick={onClose} />
      <aside
        data-atelier="true"
        className="w-full max-w-md bg-atelier-bg border-l border-atelier-line h-full overflow-y-auto flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-atelier-line sticky top-0 bg-atelier-bg z-10 atelier-glass-nav">
          <div className="flex items-center gap-2.5">
            <FolderOpen className="w-4 h-4 text-atelier-coral" strokeWidth={1.6} />
            <h3
              className="text-lg text-atelier-ink tracking-tight"
              style={{ fontFamily: "'Noto Serif TC', Georgia, serif", fontWeight: 500 }}
            >
              我的輪播專案
            </h3>
            <span className="atelier-caption tabular-nums">{projects.length.toString().padStart(2, '0')}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-atelier-surface2 rounded-full transition-colors"
            data-testid="carousel-projects-close"
          >
            <X className="w-4 h-4 text-atelier-mute" strokeWidth={1.6} />
          </button>
        </div>
        <div className="flex-1 p-3 space-y-2 atelier-stagger">
          {sorted.length === 0 && (
            <div className="text-center py-14">
              <div className="atelier-oil-loader mx-auto mb-5" style={{ width: 48, height: 48 }} />
              <p className="atelier-caption mb-1.5">No Projects</p>
              <p className="text-[13px] text-atelier-mute leading-relaxed">
                暫無專案 — 開始第一個輪播吧
              </p>
            </div>
          )}
          {sorted.map((p) => {
            const gen = !!p._generating;
            return (
              <button
                key={p.id}
                onClick={() => {
                  if (gen) {
                    toast.info('呢個專案正在生成中…完成後可以打開');
                    return;
                  }
                  onOpen(p.id, p.variant === 'branded' ? 'branded' : 'clean');
                }}
                disabled={gen}
                className={`w-full text-left atelier-card p-4 block relative group transition-all duration-300 ${
                  gen
                    ? 'border-atelier-coral/50 bg-atelier-coral/[0.04] cursor-wait'
                    : 'hover:border-atelier-coral/50'
                }`}
                data-testid={`carousel-project-${p.id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  {gen ? (
                    <span className="flex items-center gap-1.5 atelier-caption text-atelier-coral">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-atelier-coral opacity-75" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-atelier-coral" />
                      </span>
                      生成中
                    </span>
                  ) : (
                    <span className="atelier-caption">
                      {p.total_pages || p.pages_count || '—'} 頁
                    </span>
                  )}
                  {typeof p.credits_charged === 'number' && p.credits_charged > 0 && (
                    <span className="atelier-caption tabular-nums ml-auto">
                      {p.credits_charged.toFixed(1)} cr
                    </span>
                  )}
                  {gen && (
                    <Loader2 className="ml-auto w-3.5 h-3.5 text-atelier-coral animate-spin" strokeWidth={2} />
                  )}
                </div>
                <h4
                  className={`text-[15px] truncate tracking-tight ${gen ? 'text-atelier-ink/80' : 'text-atelier-ink group-hover:text-atelier-coral'} transition-colors`}
                  style={{ fontFamily: "'Noto Serif TC', Georgia, serif", fontWeight: 500 }}
                >
                  {p.title || p.hook || '(無標題)'}
                </h4>
                <p className="atelier-caption mt-1 tabular-nums">
                  {new Date(p.created_at).toLocaleString('zh-TW', {
                    month: 'numeric', day: '2-digit', hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              </button>
            );
          })}
        </div>
      </aside>
    </div>
  );
};

export default Carousel;
